import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, authApi, organizationApi, Organization } from '../services/api';

interface AuthContextType {
  user: User | null;
  organization: Organization | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, firstName: string, lastName: string) => Promise<{ message: string; user_id: number; token?: string; user?: User }>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  refreshOrganization: () => Promise<void>;
  canUseAI: () => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Load user profile on mount if token exists
  useEffect(() => {
    const loadUserProfile = async () => {
      const hasToken = authApi.isAuthenticated();
      
      if (hasToken) {
        try {
          const userProfile = await authApi.getProfile();
          setUser(userProfile);
          setIsAuthenticated(true);
          
          // Load organization if user belongs to one
          if (userProfile.organization_id) {
            try {
              const org = await organizationApi.getCurrent();
              setOrganization(org);
            } catch (error) {
              console.error('Failed to load organization:', error);
            }
          }
        } catch (error) {
          console.error('Failed to load user profile:', error);
          // Clear invalid token and reset state
          authApi.logout();
          setUser(null);
          setOrganization(null);
          setIsAuthenticated(false);
        }
      } else {
        setIsAuthenticated(false);
      }
      setIsLoading(false);
    };

    loadUserProfile();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const { user: loggedInUser } = await authApi.login({ email, password });
      setUser(loggedInUser);
      setIsAuthenticated(true);
      
      // Load organization if user belongs to one
      if (loggedInUser.organization_id) {
        try {
          const org = await organizationApi.getCurrent();
          setOrganization(org);
        } catch (error) {
          console.error('Failed to load organization:', error);
        }
      }
    } catch (error) {
      throw error;
    }
  };

  const register = async (email: string, password: string, firstName: string, lastName: string) => {
    try {
      const response = await authApi.register({
        email,
        password,
        first_name: firstName,
        last_name: lastName,
      });
      
      // If token and user are returned, automatically log the user in
      if (response.token && response.user) {
        setUser(response.user);
        setIsAuthenticated(true);
        
        // Load organization if user belongs to one
        if (response.user.organization_id) {
          try {
            const org = await organizationApi.getCurrent();
            setOrganization(org);
          } catch (error) {
            console.error('Failed to load organization after registration:', error);
          }
        }
      }
      
      return response;
    } catch (error) {
      throw error;
    }
  };

  const logout = () => {
    authApi.logout();
    setUser(null);
    setOrganization(null);
    setIsAuthenticated(false);
    window.location.href = '/app/login';
  };

  const refreshUser = async () => {
    try {
      const userProfile = await authApi.getProfile();
      setUser(userProfile);
    } catch (error) {
      console.error('Failed to refresh user profile:', error);
      // If refresh fails, logout the user
      logout();
      throw error;
    }
  };

  const refreshOrganization = async () => {
    try {
      // First refresh user to get latest organization_id
      const userProfile = await authApi.getProfile();
      setUser(userProfile);
      
      // Only try to load organization if user belongs to one
      if (userProfile.organization_id) {
        const org = await organizationApi.getCurrent();
        setOrganization(org);
      } else {
        setOrganization(null);
      }
    } catch (error) {
      console.error('Failed to refresh organization:', error);
      // If refresh fails, logout the user
      logout();
      throw error;
    }
  };

  const canUseAI = () => {
    if (!user) return false;
    
    // Only the explicit permission field determines AI access
    return user.can_use_ai_tools;
  };

  const value: AuthContextType = {
    user,
    organization,
    isLoading,
    isAuthenticated,
    login,
    register,
    logout,
    refreshUser,
    refreshOrganization,
    canUseAI,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 